/*
 OK FBI — Case Difficulty Selector
 Host-only. Selects easy / medium / hard for the current case BEFORE investigation begins.
 Locks after any progress; unlocks via case-full-reset.
 Effects (client-side presentation only — never changes the culprit or case data):
   - easy:   auto-hint panel + relaxed forensic thresholds visible + red-herring badges dim
   - medium: default
   - hard:   red-herring suspects show no visual difference from real culprits; alibi tags neutralized in stream mode
*/
import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Zap, Award, Skull, Lock, RotateCcw, Info } from "lucide-react";
import { sfx } from "@/lib/sounds";
import { toast } from "sonner";

const MODES = [
  { key: "easy",   label: "سهل",    icon: Zap,   note: "توجيه أكثر · أدلة مضللة أقل · تلميحات اختيارية", color: "text-emerald-400", border: "border-emerald-700" },
  { key: "medium", label: "متوسط",  icon: Award, note: "تجربة تحقيق متوازنة كما صممت أصلاً",           color: "text-gold",        border: "border-gold" },
  { key: "hard",   label: "صعب",    icon: Skull, note: "أدلة مضللة أقوى · تلميحات أقل · تناقضات أدق",  color: "text-red-400",     border: "border-red-700" },
];

export function DifficultySelector({ caseId, onChange }) {
  const [state, setState] = useState({ difficulty: "medium", locked: false });
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    if (!caseId) return;
    try {
      const { data } = await api.get(`/session/case-difficulty/${caseId}`);
      setState({ difficulty: data.difficulty, locked: data.locked });
      onChange?.(data.difficulty);
    } catch { /* fallback: keep default difficulty on 401/403/network error */ }
  }, [caseId, onChange]);
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, [load]);

  const pick = async (key) => {
    if (state.locked || busy) return;
    setBusy(true);
    try {
      await api.post("/session/case-difficulty", { case_id: caseId, difficulty: key });
      setState(s => ({ ...s, difficulty: key }));
      onChange?.(key);
      sfx.click(); toast.success(`تم اختيار: ${MODES.find(m => m.key === key)?.label}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذّر تغيير الصعوبة");
      load();
    } finally { setBusy(false); }
  };

  return (
    <div className="panel border-metal p-4" data-testid="difficulty-selector">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="label-caps">مستوى الصعوبة</div>
          {state.locked && (
            <span className="text-[9px] text-amber-500 font-mono-ar flex items-center gap-1" data-testid="difficulty-locked">
              <Lock size={10} /> مقفل — استخدم "إعادة التحقيق من البداية"
            </span>
          )}
        </div>
        <div className="text-[9px] text-zinc-600 font-mono-ar">DIFFICULTY · الجاني لا يتغير أبداً</div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {MODES.map(m => {
          const sel = state.difficulty === m.key;
          const Icon = m.icon;
          return (
            <button key={m.key}
              onClick={() => pick(m.key)}
              disabled={state.locked || busy}
              data-testid={`difficulty-${m.key}`}
              className={`text-right p-3 border transition-all ${sel ? `${m.border} bg-[rgba(255,255,255,0.03)]` : "border-metal"} ${state.locked ? "opacity-60 cursor-not-allowed" : "hover:border-gold/60"}`}>
              <div className="flex items-center justify-between mb-1">
                <Icon size={14} className={sel ? m.color : "text-zinc-500"} />
                {sel && <span className={`text-[9px] font-mono-ar ${m.color}`}>ACTIVE</span>}
              </div>
              <div className={`font-cairo text-sm ${sel ? m.color : "text-zinc-300"}`}>{m.label}</div>
              <div className="text-[10px] text-zinc-500 mt-1 leading-tight">{m.note}</div>
            </button>
          );
        })}
      </div>
      <div className="mt-2 text-[10px] text-zinc-600 flex items-center gap-1"><Info size={9} /> الصعوبة تؤثر على العرض فقط — القضية تبقى منطقياً قابلة للحل في كل الأوضاع.</div>
    </div>
  );
}


// ============ Difficulty helpers used by other UI ============
export const easyHints = {
  "case-001": [
    "لاحظ الفترة التي تعطلت فيها كاميرا الطابق 13 (~02:11) — من كان يمكنه الوصول للجناح في هذه اللحظة؟",
    "تحقق من دخول موظفي الفندق مقابل تسلسل زيارات الضيوف الخارجيين.",
    "الأدلة المرتبطة بالباب الخلفي والمصاعد أهم مما تبدو.",
  ],
  "case-002": [
    "من كان آخر من دخل مكتب الضحية بعد مغادرتها؟",
    "تتبع سيارة الضحية بعد الحادثة يقود لأدلة حاسمة.",
  ],
  "case-003": [
    "من غادر موقعه أثناء انطفاء الأنوار؟",
    "القفازات في اليدين ليست دائماً بريئة.",
  ],
  "case-004": [
    "المضيفة قد تكون مفتاح الحقيقة. راجع القهوة الخاصة.",
    "الكاميرا المعطلة بين العربتين 4 و 5 هي منطقة الجريمة الحقيقية.",
  ],
  "case-005": [
    "من حضّر آخر مشروب للضحية؟",
    "من الذي غيّب كاميرا مكتب رامي؟",
  ],
};

export function useCaseDifficulty(caseId) {
  const [d, setD] = useState("medium");
  useEffect(() => {
    if (!caseId) return;
    let stop = false;
    const load = () => api.get(`/session/case-difficulty/${caseId}`).then(r => { if (!stop) setD(r.data.difficulty || "medium"); }).catch(() => {});
    load();
    const t = setInterval(load, 6000);
    return () => { stop = true; clearInterval(t); };
  }, [caseId]);
  return d;
}
